import type { CSSProperties, FormEvent, ReactNode } from 'react';
import { COULEURS, POLICE_EQUIPE, styleBouton, styleBoutonPrincipal } from '../../app/styles.js';
import { questionBeta, QUESTIONS_BETA, type CleBeta } from '../../../../../packages/contracts/src/beta/index.js';
import { definitionParametre, type CleLegale } from '../../../../../packages/contracts/src/legal/index.js';
import type { ControleurAdmin, EtatAdmin } from './controleur.js';
import {
  STATUTS_PARAMETRE,
  avertissementSuppression,
  dureeMinutes,
  formaterInstant,
  formeModifiable,
  initiales,
  libellePortee,
  libelleRole,
  libelleValeur,
  libelleAcces,
  resumeEtude,
  saisieEtude,
  saisieSeuil,
  type LireChamp,
  valeurEnVigueur,
} from './modele.js';
import type { Etude, ParametreLegal, Reglage, SessionIncarnation, Utilisateur } from './types.js';

/**
 * Écran D10 : Administration et « voir en tant que » (maquette D10-Admin-voir-en-tant-que). Équipe MAAT seulement,
 * vouvoiement, aucune donnée réelle. Présentation pure : tout l'état et toutes les actions viennent du contrôleur.
 */
export type ActionsAdmin = Pick<
  ControleurAdmin,
  | 'choisir'
  | 'saisirMotif'
  | 'choisirMode'
  | 'demarrer'
  | 'terminer'
  | 'tenterAction'
  | 'demanderSuppressionEtude'
  | 'ajouterEtude'
  | 'versionner'
  | 'confirmer'
  | 'annuler'
> & { charger?: () => void };

export interface PropsPageAdministration {
  etat: EtatAdmin;
  actions: ActionsAdmin;
  /** Date du jour (AAAA-MM-JJ) pour choisir la valeur en vigueur ; par défaut, aujourd'hui. */
  aujourdhui?: string;
  /** Titre de l'écran dans le contrat (D10). */
  titre?: string;
}

const lireFormulaire = (formulaire: HTMLFormElement): LireChamp => {
  const donnees = new FormData(formulaire);
  return (nom) => String(donnees.get(nom) ?? '');
};

const carte: CSSProperties = {
  border: `1px solid ${COULEURS.bordure}`,
  borderRadius: 12,
  background: COULEURS.blanc,
  padding: 16,
  marginBottom: 16,
};
const ligne: CSSProperties = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, padding: '8px 0' };
const champ: CSSProperties = {
  minHeight: 44,
  padding: '0 12px',
  borderRadius: 8,
  border: `1px solid ${COULEURS.bordure}`,
  font: 'inherit',
};

const Bloc = ({ titre, children, soustitre }: { titre: string; soustitre?: string; children: ReactNode }) => (
  <section style={carte} aria-label={titre}>
    <h2 style={{ margin: '0 0 4px', fontSize: 20, color: COULEURS.marine }}>{titre}</h2>
    {soustitre ? <p style={{ margin: '0 0 8px', color: COULEURS.ardoise }}>{soustitre}</p> : null}
    {children}
  </section>
);

function BoiteConfirmation({ etat, actions }: { etat: EtatAdmin; actions: ActionsAdmin }) {
  const confirmation = etat.confirmation;
  if (!confirmation) return null;
  const cible = etat.session?.impersonation?.targetDisplayName ?? 'cette personne';
  const suppression = confirmation.type === 'suppression-etude';
  const titre = suppression ? 'Supprimer cette étude ?' : `Confirmer l’action en tant que ${cible}`;
  const avertissement = suppression ? avertissementSuppression(confirmation.etude) : (confirmation.avertissement ?? null);
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="admin-confirmation-titre"
      style={{ ...carte, borderColor: COULEURS.ambre, background: COULEURS.ambreFond }}
    >
      <h2 id="admin-confirmation-titre" style={{ margin: '0 0 8px', fontSize: 20 }}>
        {titre}
      </h2>
      {suppression ? (
        <p>{`L’étude « ${confirmation.etude.name} » sera retirée de la liste. Cette opération est tracée au journal.`}</p>
      ) : (
        <p>{`L’action « ${confirmation.libelle ?? confirmation.action} » sera faite par ${etat.session?.user.displayName ?? 'vous'} en tant que ${cible}, et journalisée comme telle.`}</p>
      )}
      {avertissement ? <p style={{ color: COULEURS.rouge, fontWeight: 600 }}>{avertissement}</p> : null}
      <div style={ligne}>
        <button type="button" style={styleBoutonPrincipal} onClick={() => void actions.confirmer()}>
          Confirmer
        </button>
        <button type="button" style={styleBouton} onClick={() => actions.annuler()}>
          Annuler
        </button>
      </div>
    </div>
  );
}

function SessionEnCours({ etat, actions }: { etat: EtatAdmin; actions: ActionsAdmin }) {
  const courante = etat.session?.impersonation;
  if (!courante) return null;
  const mode = courante.mode === 'read' ? 'lecture seule · session tracée' : 'actions tracées · session tracée';
  return (
    <div role="region" aria-label="Session en cours" style={{ ...carte, background: COULEURS.indigo, color: COULEURS.blanc }}>
      <p style={{ margin: '0 0 8px' }}>{`Session en cours : vous voyez Lakou en tant que ${courante.targetDisplayName} · ${mode}`}</p>
      <div style={ligne}>
        <button type="button" style={styleBouton} onClick={() => void actions.terminer()}>
          Terminer la session
        </button>
        {courante.mode === 'read' ? (
          <button type="button" style={styleBouton} onClick={() => actions.tenterAction('essai.lecture-seule', {})}>
            Essayer une action
          </button>
        ) : null}
      </div>
    </div>
  );
}

function Utilisateurs({ etat, actions }: { etat: EtatAdmin; actions: ActionsAdmin }) {
  const utilisateurs = etat.donnees?.utilisateurs ?? [];
  const choisi = utilisateurs.find((u) => u.id === etat.selection);
  const enCours = etat.session?.impersonation != null;
  const modeChoisi = etat.modeChoisi ?? 'read';
  return (
    <Bloc titre="Utilisateurs et « voir en tant que »" soustitre="Choisissez une personne pour voir l’outil exactement comme elle">
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {utilisateurs.map((u: Utilisateur) => (
          <li key={u.id} style={ligne}>
            <span aria-hidden="true" style={{ fontWeight: 600, minWidth: 32 }}>
              {initiales(u.displayName)}
            </span>
            <span style={{ flex: 1 }}>
              <strong>{u.displayName}</strong>
              <br />
              {libellePortee(u)}
            </span>
            {u.roles.some((r) => r.role === 'admin') ? null : (
              <button
                type="button"
                style={styleBouton}
                aria-label={`Voir en tant que ${u.displayName}`}
                disabled={enCours}
                onClick={() => actions.choisir(u.id)}
              >
                Voir en tant que
              </button>
            )}
          </li>
        ))}
      </ul>
      {choisi ? (
        <aside aria-label="Aperçu" style={{ ...carte, background: COULEURS.surface }}>
          <h3 style={{ margin: '0 0 8px' }}>{`Aperçu de la vue de ${choisi.displayName}`}</h3>
          <p style={{ margin: '0 0 8px' }}>{`${libelleRole(choisi.roles[0]?.role ?? '')} · ${
            modeChoisi === 'act' ? 'actions tracées' : 'lecture seule'
          }, session tracée au journal.`}</p>
          <p style={{ margin: '0 0 8px' }}>Toute action faite en tant que cette personne demande une confirmation explicite.</p>
          <fieldset style={{ border: 0, margin: '0 0 8px', padding: 0 }}>
            <legend>Mode de la session</legend>
            {(
              [
                ['read', 'Lecture seule (recommandé) : vous voyez, sans rien modifier'],
                ['act', 'Actions tracées : chaque écriture demande votre confirmation et est journalisée'],
              ] as const
            ).map(([mode, libelle]) => (
              <label key={mode} style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44 }}>
                <input type="radio" name="mode" value={mode} checked={modeChoisi === mode} onChange={() => actions.choisirMode(mode)} />
                {libelle}
              </label>
            ))}
          </fieldset>
          <label style={{ display: 'block', marginBottom: 8 }}>
            Motif de la consultation
            <input
              name="motif"
              type="text"
              value={etat.motif}
              style={{ ...champ, display: 'block', width: '100%', boxSizing: 'border-box' }}
              onChange={(ev) => actions.saisirMotif(ev.target.value)}
            />
          </label>
          <button type="button" style={styleBoutonPrincipal} disabled={etat.occupe} onClick={() => void actions.demarrer()}>
            {modeChoisi === 'act' ? 'Commencer la session en actions tracées' : 'Commencer la session en lecture seule'}
          </button>
        </aside>
      ) : null}
    </Bloc>
  );
}

function FormulaireEtude({ actions }: { actions: ActionsAdmin }) {
  const envoyer = (ev: FormEvent<HTMLFormElement>): void => {
    ev.preventDefault();
    void actions.ajouterEtude(saisieEtude(lireFormulaire(ev.currentTarget)));
  };
  return (
    <form onSubmit={envoyer} style={ligne} aria-label="Ajouter une étude">
      <input name="nom" required placeholder="Nom de l’étude" aria-label="Nom de l’étude" style={champ} />
      <input name="adresse" placeholder="Adresse" aria-label="Adresse" style={champ} />
      <input name="notaire" placeholder="Notaire titulaire" aria-label="Notaire titulaire" style={champ} />
      <button type="submit" style={styleBouton}>
        Ajouter
      </button>
    </form>
  );
}

function Etudes({ etat, actions }: { etat: EtatAdmin; actions: ActionsAdmin }) {
  return (
    <Bloc titre="Études et clercs">
      <p style={{ margin: 0, color: COULEURS.ardoise }}>
        Un accès par ligne : une personne, un dossier ou une étude. Vous pouvez en donner plusieurs pour un même dossier.
      </p>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {(etat.donnees?.etudes ?? []).map((e: Etude) => (
          <li key={e.id} style={ligne}>
            <span style={{ flex: 1 }}>
              <strong>{e.name}</strong>
              <br />
              {resumeEtude(e)}
              <br />
              <span style={{ color: COULEURS.ardoise }}>{libelleAcces(e.members.length)}</span>
            </span>
            <button
              type="button"
              style={styleBouton}
              aria-label={`Supprimer l’étude ${e.name}`}
              onClick={() => actions.demanderSuppressionEtude(e.id)}
            >
              Supprimer
            </button>
          </li>
        ))}
      </ul>
      <FormulaireEtude actions={actions} />
    </Bloc>
  );
}

function nomParametre(cle: string): { libelle: string; forme: string; statut: string } {
  try {
    const d = definitionParametre(cle as CleLegale);
    return { libelle: d.libelle, forme: d.forme, statut: STATUTS_PARAMETRE[d.statut] ?? d.statut };
  } catch {
    return { libelle: cle, forme: 'inconnue', statut: 'à valider' };
  }
}

function Seuils({ etat, actions, jour }: { etat: EtatAdmin; actions: ActionsAdmin; jour: string }) {
  const parametres: ParametreLegal[] = etat.donnees?.parametres ?? [];
  const cles = [...new Set(parametres.map((p) => p.key))];
  const modifiables = cles.filter((c) => formeModifiable(nomParametre(c).forme));
  const envoyer = (ev: FormEvent<HTMLFormElement>): void => {
    ev.preventDefault();
    void actions.versionner(saisieSeuil(lireFormulaire(ev.currentTarget)));
  };
  return (
    <Bloc titre="Seuils légaux (paramètres)">
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {cles.map((cle) => {
          const nom = nomParametre(cle);
          const courant = valeurEnVigueur(parametres, cle, jour);
          return (
            <li key={cle} style={ligne}>
              <span style={{ flex: 1 }}>{nom.libelle}</span>
              <strong>{courant ? libelleValeur(nom.forme, courant.value, courant.effectiveTo) : 'aucune valeur en vigueur'}</strong>
              <span style={{ color: COULEURS.ardoise }}>{`(${nom.statut})`}</span>
            </li>
          );
        })}
      </ul>
      <p style={{ color: COULEURS.ardoise }}>Modifiables si la loi change. Chaque modification est datée.</p>
      {modifiables.length > 0 ? (
        <form onSubmit={envoyer} style={ligne} aria-label="Ajouter une valeur datée">
          <select name="cle" aria-label="Paramètre" style={champ}>
            {modifiables.map((c) => (
              <option key={c} value={c}>
                {nomParametre(c).libelle}
              </option>
            ))}
          </select>
          <input name="valeur" type="number" step="any" required aria-label="Nouvelle valeur" style={champ} />
          <input name="effet" type="date" required aria-label="Date d’effet" style={champ} />
          <input name="note" placeholder="Source ou note" aria-label="Source ou note" style={champ} />
          <button type="submit" style={styleBouton}>
            Enregistrer le nouveau seuil
          </button>
        </form>
      ) : null}
    </Bloc>
  );
}

const valeurTexte = (v: unknown): string => (typeof v === 'string' ? v : JSON.stringify(v));

function libelleReglage(cle: string): string {
  const q = QUESTIONS_BETA.find((x) => x.cle === cle);
  return q ? q.question : cle;
}

function ReglagesBeta({ etat }: { etat: EtatAdmin }) {
  const reglages: Reglage[] = etat.donnees?.reglages ?? [];
  const repondues = new Set(reglages.map((r) => r.key));
  const ouvertes = QUESTIONS_BETA.filter((q) => !repondues.has(q.cle));
  return (
    <Bloc titre="Réglages BETA appris">
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {reglages.map((r) => (
          <li key={r.key} style={ligne}>
            <span style={{ flex: 1 }}>{`${libelleReglage(r.key)} :`}</span>
            <strong>{valeurTexte(r.value)}</strong>
            <span style={{ color: COULEURS.ardoise }}>{`(${r.updatedByDisplayName}, ${formaterInstant(r.updatedAt)})`}</span>
          </li>
        ))}
      </ul>
      {ouvertes.length > 0 ? (
        <details>
          <summary style={{ minHeight: 44 }}>{`Questions sans réponse (${ouvertes.length})`}</summary>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {ouvertes.map((q) => (
              <li key={q.cle} style={ligne}>
                <span style={{ flex: 1 }}>{`${questionBeta(q.cle as CleBeta).question} :`}</span>
                <span style={{ color: COULEURS.ardoise }}>pas encore répondu</span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </Bloc>
  );
}

function Historique({ etat }: { etat: EtatAdmin }) {
  const donnees = etat.donnees;
  if (!donnees || donnees.historique.length === 0) return null;
  const nom = (id: string): string => donnees.utilisateurs.find((u) => u.id === id)?.displayName ?? 'personne inconnue';
  return (
    <Bloc titre="Historique des sessions « voir en tant que »">
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {donnees.historique.map((s: SessionIncarnation) => (
          <li key={s.id} style={ligne}>
            {`${nom(s.actorUserId)} en tant que ${nom(s.targetUserId)} · début ${formaterInstant(s.startedAt)} · fin ${
              s.endedAt ? formaterInstant(s.endedAt) : 'en cours'
            } · durée ${dureeMinutes(s.startedAt, s.endedAt)}`}
          </li>
        ))}
      </ul>
    </Bloc>
  );
}

export function PageAdministration({ etat, actions, aujourdhui, titre = 'Administration' }: PropsPageAdministration) {
  const jour = aujourdhui ?? new Date().toISOString().slice(0, 10);
  let corps: ReactNode;
  if (etat.chargement === 'init' || etat.chargement === 'en-cours') {
    corps = <p role="status">Chargement de l’administration…</p>;
  } else if (etat.chargement === 'erreur' || !etat.donnees) {
    corps = (
      <div>
        <p role="alert">{etat.message ?? 'Impossible de charger l’administration. Réessayez dans un instant.'}</p>
        {etat.reessayable === false ? null : (
          <button type="button" style={styleBouton} onClick={() => actions.charger?.()}>
            Réessayer
          </button>
        )}
      </div>
    );
  } else {
    corps = (
      <>
        <SessionEnCours etat={etat} actions={actions} />
        <BoiteConfirmation etat={etat} actions={actions} />
        <Utilisateurs etat={etat} actions={actions} />
        <Etudes etat={etat} actions={actions} />
        <Seuils etat={etat} actions={actions} jour={jour} />
        <ReglagesBeta etat={etat} />
        <Historique etat={etat} />
      </>
    );
  }
  return (
    <section
      data-ecran="D10"
      data-lot="L59"
      style={{ fontFamily: POLICE_EQUIPE, fontSize: 16, lineHeight: 1.5, color: COULEURS.marineFonce }}
    >
      <h1 style={{ fontSize: 28, color: COULEURS.marine }}>{titre}</h1>
      {etat.message && etat.chargement === 'pret' ? <p role="status">{etat.message}</p> : null}
      {corps}
    </section>
  );
}
